import { Point } from '../../math/point';
import { vector, add, Vector, subtract } from '../../math/vector';
import { Line } from 'src/app/math/line';
import { RED_LINE, LineStyle } from 'src/app/display/lines';
import { WeaponType, Weapon } from 'src/app/model/weapon';
import { World } from '../../model/world'
import { GameUpdate, GameUpdateState } from '../game-update';
import { Graphics } from 'src/app/display/graphics';
import { PointMotion } from './point-motion';
import clone from 'immutability-helper'
import { RED, BLUE, WHITE, BLACK, GREEN, YELLOW } from 'src/app/display/color';
import { Ship } from 'src/app/model/ship';

abstract class WeaponFireState extends GameUpdateState {
  protected readonly sound: string
  protected readonly pointMotion: PointMotion
  protected p: Point

  constructor(protected readonly source: Ship, protected readonly target: Ship, protected readonly weapon: Weapon, speed: number) {
    super()

    //
    const r = 5
    const theta = weapon.mountPoint + source.heading
    const x = r * Math.cos(theta)
    const y = r * Math.sin(theta)
    const v = new Vector(x, y)
    this.p = add(this.source.position, v)

    this.pointMotion = new PointMotion(this.p, target.position, speed)
  }

  initialDraw(world: World, graphics: Graphics): void {
    // graphics.play(this.sound)
  }

  completed(world: World): World {
    // return this._context.completed(world)

    const source = world.getShip(this.source.name)
    const target = world.getShip(this.target.name)

    if (source !== undefined && target !== undefined && target.hullPoints >= 0) {
      const targetRange = vector(target.position, source.position).length()
      console.log(`weapon fire damage`)

      const [misses, hits, auto] = this.weapon.damage(targetRange)
      console.log(`${misses}-${hits}-${auto}`)

      const source0 = source.setWeapon(this.weapon.fire(hits, auto))
      const world0 = world.setShip(source0)
      const target0 = target.damage(source0.position, hits, auto)
      const world1 = world0.setShip(target0)
      if (target0.hullPoints <= 0) {
        return this.changeState(world1, new ShipDestroyedState(target0))
      }


      console.log(``)

      return super.completed(world1)
    }

    if (source !== undefined) {
      const source0 = source.setWeapon(this.weapon.fire(0, 0))
      const world0 = world.setShip(source0)
      return super.completed(world0)
    }

    return super.completed(world)
  }
}

function createWeaponFire(source: Ship, target: Ship, weapon: Weapon): GameUpdateState {
  switch (weapon.weaponType) {
    case WeaponType.Phaser1: return new PhaserFireState(source, target, weapon)
    case WeaponType.Phaser2: return new Phaser2FireState(source, target, weapon)
    case WeaponType.Phaser3: return new Phaser2FireState(source, target, weapon)
    case WeaponType.Disruptor: return new Phaser2FireState(source, target, weapon)
    case WeaponType.Photon: return new PhotonFireState(source, target, weapon)
    // case WeaponType.Photon: return new Phaser2FireState(source, target, weapon)

  }
}

export class WeaponFireStateMachine extends GameUpdate {
  constructor(readonly source: Ship, readonly target: Ship, weapon: Weapon) {
    super(createWeaponFire(source, target, weapon))
  }
}

class PhaserFireState extends WeaponFireState {
  protected readonly sound = 'phaser1'
  protected line: Line

  constructor(source: Ship, target: Ship, weapon: Weapon) {
    super(source, target, weapon, 0.1)
    this.line = new Line(this.p, this.p)
  }

  draw(world: World, graphics: Graphics): void {
    const style1 = new LineStyle(RED, 5)
    graphics.lineStyle(style1)
    graphics.line(this.line.p0, this.line.p1)
    const style2 = new LineStyle(BLACK, 2)
    graphics.lineStyle(style2)
    graphics.line(this.line.p0, this.line.p1)
  }

  update(world: World, elapsed: number): World {
    const target0 = world.getShip(this.target.name)
    if (target0 !== undefined && target0.hullPoints >= 0) {
      const [p1, remaining] = this.pointMotion.arrive(this.line.p1, elapsed)
      this.line = clone(this.line, {p1: {$set: p1}})
      if (remaining > 0) {
        return this.completed(world)
      }
    } else {
      const p1 = this.pointMotion.move(this.line.p1, elapsed)
      this.line = clone(this.line, {p1: {$set: p1}})
      if (!world.grid.rectangle().within(this.line.p1)) {
        return this.completed(world)
      }
    }
    return world
  }
}

class PhotonFireState extends WeaponFireState {
  protected readonly sound = 'photon1'

  constructor(source: Ship, target: Ship, weapon: Weapon) {
    super(source, target, weapon, 0.025)
  }

  update(world: World, elapsed: number): World {
    const target0 = world.getShip(this.target.name)
    if (target0 !== undefined && target0.hullPoints >= 0) {
      const [p1, remaining] = this.pointMotion.arrive(this.p, elapsed)
      this.p = p1

      if (remaining > 0) {
        return this.completed(world)
      }
    } else {
      this.p = this.pointMotion.move(this.p, elapsed)
      if (!world.grid.rectangle().within(this.p)) {
        return this.completed(world)
      }
    }

    return world
  }

  draw(world: World, graphics: Graphics): void {
    graphics.fillStyle(YELLOW)
    graphics.circle(this.p, 2)
    graphics.fillStyle(RED)
    graphics.circle(this.p, 1)
  }
}

class Phaser2FireState extends WeaponFireState {
  protected line: Line
  protected readonly sound = 'phaser1'

  constructor(source: Ship, target: Ship, weapon: Weapon) {
    super(source, target, weapon, 0.05)
    this.line = new Line(this.p, this.p)
  }

  update(world: World, elapsed: number): World {
    const target0 = world.getShip(this.target.name)
    if (target0 !== undefined && target0.hullPoints >= 0) {
      const [p1, remaining] = this.pointMotion.arrive(this.line.p1, elapsed)
      this.line = clone(this.line, {p1: {$set: p1}})

      const v2 = vector(this.line.p0, this.line.p1)
      if (v2.length() > 10) {
        const p0 = subtract(this.line.p1, this.pointMotion.p.normalize(10))
        // const [p0, remaining] = this.pointMotion.arrive(this.line.p0, elapsed)
        this.line = clone(this.line, {p0: {$set: p0}})
      }
      if (remaining > 0) {
        return this.completed(world)
      }
    } else {
      const p1  = this.pointMotion.move(this.line.p1, elapsed)
      this.line = clone(this.line, {p1: {$set: p1}})

      const v2 = vector(this.line.p0, this.line.p1)
      if (v2.length() > 10) {
        const p0  = this.pointMotion.move(this.line.p0, elapsed)
        this.line = clone(this.line, {p0: {$set: p0}})
      }
      if (!world.grid.rectangle().within(this.line.p1)) {
        return this.completed(world)
      }
    }

    return world
  }

  draw(world: World, graphics: Graphics): void {
    const style1 = new LineStyle(BLUE, 8)
    graphics.lineStyle(style1)
    graphics.line(this.line.p0, this.line.p1)
    const style2 = new LineStyle(WHITE, 1)
    graphics.lineStyle(style2)
    graphics.line(this.line.p0, this.line.p1)
  }
}


class ShipDestroyedState extends GameUpdateState {
  count: number = 2
  dir: number = -1
  alpha: number = 1
  constructor(protected readonly target: Ship) {
    super()
  }

  enterState(world: World): World {
    const target0 = world.getShip(this.target.name)
    if (target0 !== undefined) {
      const target1 = target0.setShields([0, 0, 0, 0])
      return world.setShip(target1)
    }
    return world
  }

  update(world: World, elapsed: number): World {
    if (this.count > 0) {
      if (this.alpha > 1 && this.dir === 1) {
        this.dir = -1
      } else if (this.alpha >= 0 ) {
        this.alpha += this.dir * 0.05
      } else {
        this.dir = 1
        this.count -= 1
        this.alpha = 0
      }
      return world
    } else {
      const world0 = world.destroyShip(this.target.name)
      return this.completed(world0)
    }
  }

  draw(world: World, graphics: Graphics): void {
    const image = world.getShip(this.target.name)?.sprite.image
    if (image !== undefined) {
      image.setAlpha(this.alpha)
      if (this.count === 2) {
        image.setTintFill(0xFFFFFF)
      } else if (this.count === 1) {
        image.setTintFill(0xFF0000)
      }
    }
  }
}
